// the utility funtions mentioned below are implemented using test logic for formatting data as mock data is being used currently.
// Update the formatting logic in future as per the response from API
import { sortBy } from "lodash";
import reportConstants from "./constants";
import { createGanttStop } from "./Gantt/GanttHelper";
import { addMinutes, format, differenceInMinutes } from "date-fns";
import { v4 as uuidv4 } from "uuid";

const getStopLabel = (stop, dispatchDate) => {
  const { DISPATCH_STOP_TYPES, REPORT_STOP_TYPE_LABEL } = reportConstants;
  switch (stop.stopType) {
    case DISPATCH_STOP_TYPES.FUELBREAK:
      return REPORT_STOP_TYPE_LABEL.fuelBreak;
    case DISPATCH_STOP_TYPES.LUNCHBREAK:
      return REPORT_STOP_TYPE_LABEL.lunchBreak;
    case DISPATCH_STOP_TYPES.PICKUP:
      return getPickupStopTypeDetails(stop, dispatchDate).stopLabel;
    case DISPATCH_STOP_TYPES.DELIVERY:
      return getDeliveryStopTypeDetails(stop, dispatchDate).stopLabel;
    case DISPATCH_STOP_TYPES.TRAILERHOOK:
      return REPORT_STOP_TYPE_LABEL.trailerHook;
    case DISPATCH_STOP_TYPES.TRAILERDROP:
      return REPORT_STOP_TYPE_LABEL.trailerDrop;
    default:
      return "";
  }
};

const getStopType = (stop, dispatchDate) => {
  const { DISPATCH_STOP_TYPES, REPORT_STOP_TYPES } = reportConstants;
  switch (stop.stopType) {
    case DISPATCH_STOP_TYPES.FUELBREAK:
      return REPORT_STOP_TYPES.delay;
    case DISPATCH_STOP_TYPES.LUNCHBREAK:
      return REPORT_STOP_TYPES.delay;
    case DISPATCH_STOP_TYPES.PICKUP:
      return getPickupStopTypeDetails(stop, dispatchDate).stopType;
    case DISPATCH_STOP_TYPES.DELIVERY:
      return getDeliveryStopTypeDetails(stop, dispatchDate).stopType;
    case DISPATCH_STOP_TYPES.TRAILERHOOK:
      return REPORT_STOP_TYPES.trailerHook;
    case DISPATCH_STOP_TYPES.TRAILERDROP:
      return REPORT_STOP_TYPES.trailerDrop;
    default:
      return "";
  }
};

const getSingleBillDelStopDetails = (stop) => {
  const { shipments } = stop;
  const { REPORT_STOP_TYPES, REPORT_STOP_TYPE_LABEL } = reportConstants;

  const deliveryCode = shipments[0].deliveryCode;
  switch (deliveryCode) {
    case "RTN":
      return {
        stopType: REPORT_STOP_TYPES.deliveryReturn,
        stopLabel: REPORT_STOP_TYPE_LABEL.deliveryReturn,
      };
    case "DE":
      return {
        stopType: REPORT_STOP_TYPES.deliveryReturn,
        stopLabel: REPORT_STOP_TYPE_LABEL.deliveryException,
      };
    default:
      return {
        stopType: REPORT_STOP_TYPES.delivery,
        stopLabel: REPORT_STOP_TYPE_LABEL.delivery,
      };
  }
};

const getUniqueShipmentDeliveryCodes = (stop) => {
  const { shipments } = stop;
  return [
    ...new Set(shipments.map((shipment) => shipment.returnCode)),
    ...new Set(shipments.map((shipment) => shipment.exceptionCode)),
  ].filter((data) => data);
};

const getMultipleBillDelStopLabel = (stop) => {
  const { REPORT_STOP_TYPE_LABEL } = reportConstants;
  if (stop.shipments?.some((shipment) => shipment.deliveryCode === "RTN")) {
    return REPORT_STOP_TYPE_LABEL.deliveryReturn;
  }
  return REPORT_STOP_TYPE_LABEL.deliveryException;
};

const getMultipleBillDelStopDetails = (stop) => {
  const { REPORT_STOP_TYPES, REPORT_STOP_TYPE_LABEL } = reportConstants;
  const multipleBillsStatusCodes = getUniqueShipmentDeliveryCodes(stop);
  return {
    stopType: multipleBillsStatusCodes.length
      ? REPORT_STOP_TYPES.deliveryReturn
      : REPORT_STOP_TYPES.delivery,
    stopLabel: multipleBillsStatusCodes.length
      ? getMultipleBillDelStopLabel(stop)
      : REPORT_STOP_TYPE_LABEL.delivery,
  };
};

const getCurrentStopDetails = (stop) => {
  const { REPORT_STOP_TYPES, REPORT_STOP_TYPE_LABEL } = reportConstants;
  if (isPickupStop(stop)) {
    return {
      stopType: REPORT_STOP_TYPES.pickup,
      stopLabel: REPORT_STOP_TYPE_LABEL.pickup,
    };
  }
  return {
    stopType: REPORT_STOP_TYPES.delivery,
    stopLabel: REPORT_STOP_TYPE_LABEL.delivery,
  };
};

const getPendingStopTypeDetails = (stop, dispatchDate) => {
  if (checkIsCurrentStop(stop)) {
    return getCurrentStopDetails(stop);
  }
  const {
    REPORT_STOP_TYPES,
    DISPATCH_STOP_STATUS_TYPES,
    REPORT_STOP_TYPE_LABEL,
  } = reportConstants;
  const { status } = getPendingStopStatus(stop, dispatchDate);
  if (isPickupStop(stop)) {
    return {
      stopType:
        status === DISPATCH_STOP_STATUS_TYPES.DELAYED
          ? REPORT_STOP_TYPES.delayedStop
          : REPORT_STOP_TYPES.pickup,
      stopLabel: REPORT_STOP_TYPE_LABEL.pickup,
    };
  }
  return {
    stopType:
      status === DISPATCH_STOP_STATUS_TYPES.DELAYED
        ? REPORT_STOP_TYPES.delayedStop
        : REPORT_STOP_TYPES.delivery,
    stopLabel: REPORT_STOP_TYPE_LABEL.delivery,
  };
};

const isPickupStop = (stop) => {
  const { DISPATCH_STOP_TYPES } = reportConstants;
  return stop.stopType === DISPATCH_STOP_TYPES.PICKUP;
};

const getPickupStopTypeDetails = (stop, dispatchDate) => {
  const { shipments } = stop;
  const { REPORT_STOP_TYPES, REPORT_STOP_TYPE_LABEL } = reportConstants;
  if (!isStopCompleted(stop)) {
    return getPendingStopTypeDetails(stop, dispatchDate);
  }
  if (shipments.length) {
    return {
      stopType: REPORT_STOP_TYPES.pickup,
      stopLabel: REPORT_STOP_TYPE_LABEL.pickup,
    };
  }
  return {
    stopType: REPORT_STOP_TYPES.pickup,
    stopLabel: REPORT_STOP_TYPE_LABEL.pickupNF,
  };
};

const getDeliveryStopTypeDetails = (stop, dispatchDate) => {
  if (isStopCompleted(stop)) {
    if (stop.shipments.length > 1) {
      return getMultipleBillDelStopDetails(stop);
    }
    return getSingleBillDelStopDetails(stop);
  }
  return getPendingStopTypeDetails(stop, dispatchDate);
};

const isStopCompleted = (stop) => {
  return stop?.isStopCompleted;
};

const getStopArrivalTime = (stop) => {
  if (isDelayStop(stop) || checkIsCurrentStop(stop)) {
    return stop.actualArrivalTime;
  }
  return isStopCompleted(stop)
    ? stop.actualArrivalTime
    : stop.plannedArrivalTime;
};

const getStopDepartureTime = (stop) => {
  if (checkIsCurrentStop(stop)) {
    const stopDuration = isDelayStop(stop)
      ? stop.delayStopDuration
      : getTotalStopTime(stop.plannedArrivalTime, stop.plannedDepartureTime);
    const departureTime = addMinutes(
      new Date(stop.actualArrivalTime),
      stopDuration
    );
    return getDateTimeFormat(departureTime);
  }
  return isStopCompleted(stop)
    ? stop.actualDepartureTime
    : stop.plannedDepartureTime;
};
const isDelayStop = (stop) => {
  const { DISPATCH_STOP_TYPES } = reportConstants;
  return [
    DISPATCH_STOP_TYPES.FUELBREAK,
    DISPATCH_STOP_TYPES.LUNCHBREAK,
  ].includes(stop.stopType);
};

const getCompletedStopsTotalDwellTime = (stopData) => {
  if (!isStopCompleted(stopData)) {
    return 0;
  }
  let actualStopDuration;
  if (isDelayStop(stopData)) {
    actualStopDuration = getTotalStopTime(
      stopData.actualArrivalTime,
      stopData.actualDepartureTime
    );
    return getTotalDwellTime(stopData.delayStopDuration, actualStopDuration);
  }
  const {
    plannedDepartureTime,
    plannedArrivalTime,
    actualArrivalTime,
    actualDepartureTime,
  } = stopData;
  const estimatedStopDuration = getTotalStopTime(
    plannedArrivalTime,
    plannedDepartureTime
  );
  actualStopDuration = getTotalStopTime(actualArrivalTime, actualDepartureTime);
  return getTotalDwellTime(estimatedStopDuration, actualStopDuration);
};

const getTotalDwellTime = (estimatedStopDuration, actualStopDuration) => {
  if (actualStopDuration > estimatedStopDuration) {
    return (actualStopDuration - estimatedStopDuration) / actualStopDuration;
  }
  return 0;
};

const checkDriverHasDelayRoutes = (driverData, dispatchDate) => {
  const { DISPATCH_STOP_STATUS_TYPES } = reportConstants;
  const { routes } = driverData;
  return routes?.some((route) => {
    return route.stops?.some(
      (stop) =>
        !stop.isStopCompleted &&
        getPendingStopStatus(stop, dispatchDate).status ===
          DISPATCH_STOP_STATUS_TYPES.DELAYED
    );
  });
};

const checkIsCurrentStop = (stop) => {
  return !isStopCompleted(stop) && stop.actualArrivalTime;
};

const getTimeStringFromDateTime = (dateTimeString) => {
  return format(dateTimeString, "HH:mm");
};

const getDateTimeFormat = (dateTimeString) => {
  return format(dateTimeString, "yyyy-MM-dd HH:mm");
};

const isStopLinkPresent = (stop) => {
  return checkIsCurrentStop(stop) || isStopCompleted(stop);
};

const isLiftGateRequired = (driverRoutes) => {
  return driverRoutes.some((route) => route.trailerType?.includes("LG"));
};

/* 
Format data from API response into a flat array of objects required for the Gantt lib
Required Format : [{id:"stop1"},{id:"stop2"},{id:"stop3"}.......]
*/
const populateFormattedData = (
  driversData,
  dispatchDate,
  isRealtimeReport = false
) => {
  const { REPORT_STOP_TYPES } = reportConstants;
  let formattedDataArr = [];
  driversData?.length &&
    driversData?.forEach((driverData) => {
      const isDriverDelayed = checkDriverHasDelayRoutes(
        driverData,
        dispatchDate
      );
      const totalRoutesWithStops = driverData.routes.filter(
        (route) => route.stops.length > 0
      );
      //1. populate driver data on report
      const driverId = uuidv4();
      formattedDataArr.push(
        getDriverData({
          driverId,
          driverData,
          isDriverDelayed,
          isLastRow: !totalRoutesWithStops.length,
          isRealtimeReport,
        })
      );
      //sort driver routes by routeStartTime.
      const sortedTotalRoutes = sortBy(totalRoutesWithStops, [
        (route) => new Date(route.routeStartTime),
      ]);

      //2. populate each driver route data
      sortedTotalRoutes?.forEach((routeData, routeIndex) => {
        const isLastRow = routeIndex === sortedTotalRoutes.length - 1;
        formattedDataArr.push(
          getRouteData({
            routeData,
            isDriverDelayed,
            isLastRow,
            driverId,
          })
        );

        const sortedStops = sortBy(routeData.stops, [
          (stop) => new Date(stop.actualArrivalTime ?? stop.plannedArrivalTime),
        ]);

        //3. Create additonal route stops on report (gate in,gate out,clock in,clock out stops)
        createAdditionalDriverStops({
          driverData,
          routeData,
          sortedStops,
          formattedDataArr,
        });

        let driverGateInStop;
        if (routeData.gateInTime) {
          driverGateInStop = createGanttStop({
            routeData,
            stopType: REPORT_STOP_TYPES.gateInStop,
          });
          formattedDataArr.push(driverGateInStop);
        }

        //4. Create Pickup and Delivery stops for each driver route.
        sortedStops.forEach((stop, stopIndex) => {
          const arrivalDateTime = getStopArrivalTime(stop);
          const departureDateTime = getStopDepartureTime(stop);
          const arrivalTime = getTimeStringFromDateTime(arrivalDateTime);
          const departureTime = getTimeStringFromDateTime(departureDateTime);
          formattedDataArr.push(
            getRouteStop({
              routeData,
              stop,
              arrivalDateTime,
              departureDateTime,
              departureTime,
              arrivalTime,
              dispatchDate,
              sortedStops,
              driverGateInStop,
              stopIndex,
            })
          );
        });
      });
    });
  return formattedDataArr;
};

const getRouteData = ({
  routeData,
  isDriverDelayed,
  isLastRow,
  driverId,
}) => {
  return {
    id: routeData.routeId,
    text: `Manifest ${routeData.manifestNumber} Trailer ${routeData.trailerNumber}`,
    parent: driverId,
    render: "split",
    isDriverDelayed: isDriverDelayed,
    isLastRow,
    isLinkPresent: false,
  };
};

const getDriverData = ({
  driverId,
  driverData,
  isDriverDelayed,
  isLastRow,
  isRealtimeReport
}) => {
  return {
    id: driverId,
    text: driverData.driverName,
    open: true,
    isParent: false,
    type: "project",
    row_height: 100,
    isDriverDelayed,
    dispatchDate: driverData.dispatchDate,
    isLinkPresent: false,
    driverRoutesData: driverData.routes,
    isLiftGateRequired: isLiftGateRequired(driverData.routes),
    isLastRow,
    isRealtimeReport
  };
};
const createAdditionalDriverStops = ({
  driverData,
  routeData,
  sortedStops,
  formattedDataArr,
}) => {
  const { REPORT_STOP_TYPES } = reportConstants;
  if (driverData.driverClockedInTime) {
    const driverClockedInStop = createGanttStop({
      driverData,
      routeData,
      stopType: REPORT_STOP_TYPES.clockInStop,
    });
    formattedDataArr.push(driverClockedInStop);
  }

  if (driverData.driverClockedOutTime) {
    const driverClockedOutStop = createGanttStop({
      driverData,
      routeData,
      stopType: REPORT_STOP_TYPES.clockOutStop,
    });
    formattedDataArr.push(driverClockedOutStop);
  }
  if (routeData.gateOutTime) {
    const driverGateOutStop = createGanttStop({
      sortedStops,
      routeData,
      stopType: REPORT_STOP_TYPES.gateOutStop,
    });
    formattedDataArr.push(driverGateOutStop);
  }
  if (routeData.driverDispatchTime) {
    const driverDispatchTimeStop = createGanttStop({
      sortedStops,
      routeData,
      stopType: REPORT_STOP_TYPES.dispatchTimeStop,
    });
    formattedDataArr.push(driverDispatchTimeStop);
  }
};

const isLastStop = (stopIndex, sortedStops) => {
  return stopIndex === sortedStops.length - 1;
};

const getRouteStop = ({
  routeData,
  stop,
  arrivalDateTime,
  departureDateTime,
  departureTime,
  arrivalTime,
  dispatchDate,
  sortedStops,
  driverGateInStop,
  stopIndex,
}) => {
  return {
    id: stop.stopId,
    text: getStopLabel(stop, dispatchDate),
    start_date: arrivalDateTime,
    end_date: departureDateTime,
    arrivalTime,
    departureTime,
    estimatedDepartureTime: departureTime,
    progress: getCompletedStopsTotalDwellTime(stop),
    parent: routeData.routeId,
    data: stop,
    stopDuration: isDelayStop(stop)
      ? stop.delayStopDuration
      : getTotalStopTime(arrivalDateTime, departureDateTime),
    routeData: routeData,
    type: getStopType(stop, dispatchDate),
    isCurrentStop: checkIsCurrentStop(stop),
    isLinkPresent: isStopLinkPresent(
      isLastStop(stopIndex, sortedStops) ? stop : sortedStops[stopIndex + 1]
    ), //check if next stop is completed.
    sourceStop: stop.stopId,
    dispatchDate,
    targetStop: isLastStop(stopIndex, sortedStops)
      ? driverGateInStop?.id
      : sortedStops[stopIndex + 1]?.stopId,
  };
};

const populateLinks = (data) => {
  const stopsToBeLinked = data?.filter((stop) => stop.isLinkPresent);
  return stopsToBeLinked.map((stop) => {
    return {
      id: stop.stopId,
      source: stop.sourceStop,
      target: stop.targetStop,
      type: 0,
    };
  });
};

const formatPickupServiceWindow = (readyTime, closeTime) => {
  return `${readyTime ?? ""}-${closeTime ?? ""}`;
};

const isPickupNoFreight = (stopData) => {
  return isPickupStop(stopData) && !stopData.shipments.length;
};

const getCompletedStopStatus = (stopData) => {
  const { DISPATCH_STOP_STATUS_TYPES } = reportConstants;
  if (isPickupNoFreight(stopData)) {
    return DISPATCH_STOP_STATUS_TYPES.PICKUP_NO_FREIGHT;
  }
  if (getCompletedStopsTotalDwellTime(stopData) !== 0) {
    return DISPATCH_STOP_STATUS_TYPES.DELAYED;
  }
  return DISPATCH_STOP_STATUS_TYPES.ONTIME;
};

const getStopServiceWindow = (stopData) => {
  return stopData?.stopType === reportConstants.DISPATCH_STOP_TYPES.DELIVERY
    ? stopData?.deliveryWindow?.replaceAll(" ", "")
    : formatPickupServiceWindow(stopData?.readyTime, stopData?.closeTime);
};

const getPendingStopStatus = (stopData, dispatchDate) => {
  const { DISPATCH_STOP_STATUS_TYPES } = reportConstants;
  const serviceWindow = getStopServiceWindow(stopData);
  if (serviceWindow) {
    const maxDeliveryTime = serviceWindow.split("-")[1];
    const deliveryTimeVsEtaDifferenceInMinutes = differenceInMinutes(
      new Date(stopData?.plannedArrivalTime),
      new Date(`${dispatchDate} ${maxDeliveryTime}`)
    );
    if (deliveryTimeVsEtaDifferenceInMinutes > 0) {
      return {
        status: DISPATCH_STOP_STATUS_TYPES.DELAYED,
        totalDelay: deliveryTimeVsEtaDifferenceInMinutes,
      };
    }
    return { status: DISPATCH_STOP_STATUS_TYPES.ONTIME, totalDelay: 0 };
  }
  return null;
};

const pluralize = (count, noun, suffix = "s") =>
  `${count} ${noun}${count > 1 ? suffix : ""}`;

const getMinutesInHourAndMinutesStr = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return `${pluralize(hours, "hour")} ${pluralize(minutes, "min")}`;
  }
  return pluralize(minutes, "min");
};

const getTotalStopTime = (arrivalTime, departureTime) => {
  const stopDurationInMinutes = differenceInMinutes(
    new Date(departureTime),
    new Date(arrivalTime)
  );
  if (stopDurationInMinutes > 0) {
    return stopDurationInMinutes;
  }
  return 0;
};

const getPendingStopStatusWithDelayDuration = (stopData, dispatchDate) => {
  const { DISPATCH_STOP_STATUS_TYPES } = reportConstants;
  const { status, totalDelay } = getPendingStopStatus(stopData, dispatchDate);
  return {
    status: status,
    totalTime:
      status === DISPATCH_STOP_STATUS_TYPES.DELAYED
        ? getMinutesInHourAndMinutesStr(totalDelay)
        : getMinutesInHourAndMinutesStr(
            getTotalStopTime(
              stopData.plannedArrivalTime,
              stopData.plannedDepartureTime
            )
          ),
  };
};

export {
  populateFormattedData,
  populateLinks,
  getPendingStopStatus,
  isStopCompleted,
  getCompletedStopStatus,
  getPendingStopStatusWithDelayDuration,
  getUniqueShipmentDeliveryCodes,
  getTotalDwellTime,
  getTotalStopTime,
  getMinutesInHourAndMinutesStr,
  checkDriverHasDelayRoutes,
  getTimeStringFromDateTime,
  isStopLinkPresent,
  pluralize,
  getStopServiceWindow,
  getStopArrivalTime,
  getStopDepartureTime,
};
